// `ver_moodboard`: el agente LISTA los moodboards de la marca y MIRA las fotos de uno,
// de a diez — ADR-2026-09-13-el-agente-ve-los-moodboards.
//
// Mismo molde que `ver_fotos` (ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez): las
// fotos no viajan en el mensaje del usuario —adjuntar una carpeta manda un PUNTERO— y el
// modelo pide los tramos que necesita; cada foto que mira se arrastra en todo el
// historial, así que mira a pedido. El `tool_result` lleva bloques de texto e imagen y
// las ve en el mismo turno.
//
// Acá está sólo lo puro (el contrato de la tool, qué carpeta pidió, qué tramo, qué
// bloques). La red —listar, traer las fotos y firmarlas— se inyecta desde
// `ChatAtlasContext`, que es quien sabe de qué empresa y marca es el chat.

import type { BloqueContenido } from '../pages/v2/lotes/verProductos';
import { ventanaDeFotos } from '../pages/v2/lotes/verFotos';
import type { AdjuntoDeMensaje } from '../pages/v2/adjuntosDelMensaje';
import { fotosDelMoodboard } from '../pages/v2/etiquetaDeAdjunto';
import type { HerramientaChat } from './tipos';

export const NOMBRE_VER_MOODBOARD = 'ver_moodboard';

/** Una carpeta, con lo justo para el catálogo y para elegirla por nombre. */
export interface MoodboardResumen {
  id: string;
  nombre: string;
  cantidadFotos: number;
}

/** Una foto lista para el bloque. `url` en null = no se pudo firmar. */
export interface FotoDeMoodboard {
  id: string;
  url: string | null;
}

export const HERRAMIENTA_VER_MOODBOARD: HerramientaChat = {
  name: NOMBRE_VER_MOODBOARD,
  description:
    'Los ESTILOS de la marca (en la app, Mi marca → Estilos; con el usuario decí "estilo", nunca "moodboard", que es sólo el nombre de esta tool): carpetas de fotos de referencia de estilo (inspiración para una campaña, no productos ni personas). Sin `moodboard` te lista las carpetas (nombre y cuántas fotos); con el nombre de una, te muestra sus fotos como imágenes dentro del resultado, de a 10, etiquetadas [ESTILO: "carpeta"]. No tiene UI, no cuesta créditos y no interrumpe: llamala junto con otras cosas en el mismo turno. Si son más de 10, volvé a llamar con `desde` (10 → desde=11), sólo los tramos que te hagan falta: cada foto que mirás se arrastra en todo el historial.\n' +
    'Usala cuando el usuario adjuntó una carpeta, nombra un estilo ("usá el estilo Verano") o pide una campaña con esa inspiración. Si la adjuntó con ALGUNAS fotos elegidas, te muestra sólo ésas, cada una con su número en la carpeta; `todas: true` la muestra entera (para curarla, o si te pide ver el resto). Qué tomar de cada foto, cuándo pasar una como referencia y cuándo armar antes la escena: skill `moodboard`.',
  input_schema: {
    type: 'object',
    properties: {
      moodboard: {
        type: 'string',
        description:
          'El nombre de la carpeta (como figura en el catálogo) o su id. Si el usuario la adjuntó, el contexto del mensaje te da el id: pasá ESE, que es exacto (puede haber dos carpetas con el mismo nombre). Omitilo para listar las carpetas.',
      },
      desde: {
        type: 'number',
        description: 'Desde qué foto empezar, contando desde 1. Default 1. Para el segundo tramo, 11.',
      },
      cantidad: {
        type: 'number',
        description: 'Cuántas mostrar (1 a 10). Default 10.',
      },
      todas: {
        type: 'boolean',
        description:
          'Si el usuario adjuntó sólo algunas fotos de la carpeta, true te muestra la carpeta entera. Default false: sólo las que eligió.',
      },
    },
  },
};

/** ¿Pidió la carpeta entera aunque el usuario haya elegido algunas? */
const pideLaCarpetaEntera = (input: unknown): boolean =>
  (input as { todas?: unknown } | null)?.todas === true;

/**
 * Qué fotos de una carpeta eligió el usuario: las del ÚLTIMO mensaje suyo que la adjuntó
 * (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). `null` = todas: la adjuntó
 * entera, o nunca la adjuntó y el agente la pidió por nombre. Volver a adjuntarla, entera
 * o con otras fotos, manda sobre la vez anterior.
 */
export function fotosElegidasDeMoodboard(
  mensajes: readonly { role: string; adjuntos?: readonly AdjuntoDeMensaje[] }[],
  moodboardId: string,
): string[] | null {
  for (let i = mensajes.length - 1; i >= 0; i--) {
    const m = mensajes[i];
    if (m.role !== 'user') continue;
    const a = m.adjuntos?.find((x) => x.kind === 'moodboard' && x.moodboardId === moodboardId);
    if (a?.kind === 'moodboard') return a.fotoIds ?? null;
  }
  return null;
}

const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/** Lo que el modelo escribió en `moodboard`, limpio; null si no pidió ninguna. */
export function moodboardPedido(input: unknown): string | null {
  const v = (input as { moodboard?: unknown } | null)?.moodboard;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * Qué carpeta pidió: por id, por nombre exacto (sin tildes ni mayúsculas), o por nombre
 * contenido si es único. Nada de adivinar entre varias: se le devuelve la lista.
 *
 * El ID va PRIMERO a propósito: dos carpetas pueden llamarse igual (la tabla no lo
 * impide) y el nombre se puede cambiar después de adjuntarla. Cuando el usuario adjuntó
 * una carpeta, el contexto del mensaje le pasa el id justamente para que no haya que
 * adivinar cuál de las dos "Verano" era.
 */
export function elegirMoodboard(
  pedido: string,
  carpetas: MoodboardResumen[],
): MoodboardResumen | null {
  const q = normalizar(pedido);
  return (
    carpetas.find((c) => c.id === pedido) ??
    carpetas.find((c) => normalizar(c.nombre) === q) ??
    (() => {
      const parciales = carpetas.filter((c) => normalizar(c.nombre).includes(q));
      return parciales.length === 1 ? parciales[0] : null;
    })()
  );
}

/**
 * De quién es un catálogo de moodboards: empresa + marca, o `null` cuando no corresponde
 * tener uno (sin cuenta todavía, o un rubro que no ve moodboards).
 *
 * Es la etiqueta que hace descartable una respuesta que llegó tarde: el catálogo se pide
 * de nuevo en cada cambio de marca, y sin comparar el alcance la respuesta de la marca
 * anterior —o la de una marca de Moda cuando el usuario ya se pasó a una que no ve
 * moodboards— pisaba el ref y el prompt terminaba anunciando carpetas de otra marca, o
 * carpetas que el agente no puede abrir porque la tool ni siquiera viaja.
 */
export function alcanceDeMoodboards(p: {
  companyId: string | null | undefined;
  brandId: string | null | undefined;
  veMoodboards: boolean;
}): string | null {
  if (!p.companyId || !p.veMoodboards) return null;
  return `${p.companyId}|${p.brandId ?? 'sin-marca'}`;
}

const fotos = (n: number) => (n === 1 ? '1 foto' : `${n} fotos`);

/** El catálogo, para el bloque volátil del prompt: una línea por carpeta. */
export function lineaDeCatalogoDeMoodboards(carpetas: MoodboardResumen[]): string {
  return carpetas.map((c) => `"${c.nombre}" (${fotos(c.cantidadFotos)})`).join(', ');
}

/** Sin `moodboard`: la lista. */
export function bloquesListaDeMoodboards(carpetas: MoodboardResumen[]): BloqueContenido[] {
  if (carpetas.length === 0) {
    return [
      {
        type: 'text',
        text: 'La marca no tiene estilos todavía. Podés crear uno vos con agregar_al_moodboard (crear_si_no_existe: true); el usuario también los arma en Mi marca → Estilos o desde la biblioteca del chat (pestaña Estilos).',
      },
    ];
  }
  return [
    {
      type: 'text',
      text: `Estilos de la marca: ${lineaDeCatalogoDeMoodboards(carpetas)}. Para ver las fotos de uno, volvé a llamar ver_moodboard con su nombre en \`moodboard\`.`,
    },
  ];
}

/** Con `moodboard` pero sin match: se le dice cuáles hay. */
export function bloquesMoodboardNoEncontrado(
  pedido: string,
  carpetas: MoodboardResumen[],
): BloqueContenido[] {
  return [
    {
      type: 'text',
      text:
        carpetas.length === 0
          ? `No hay ningún estilo que se llame "${pedido}": la marca no tiene estilos todavía.`
          : `No hay ningún estilo que se llame "${pedido}". Los que hay: ${lineaDeCatalogoDeMoodboards(carpetas)}. Volvé a llamar con uno de esos nombres.`,
    },
  ];
}

/** Las fotos de una carpeta, el tramo pedido, etiquetadas para que el modelo sepa que
 *  son inspiración y de qué carpeta. */
export function bloquesVerMoodboard(args: {
  carpeta: MoodboardResumen;
  total: number;
  desde: number;
  fotos: FotoDeMoodboard[];
  /** El número de cada foto de `fotos` EN LA CARPETA. Sin esto, `desde`, `desde + 1`…
   *  Con las elegidas no coinciden con el tramo, y `editar_moodboard` borra por éste. */
  numeros?: number[];
  /** Son sólo las que eligió el usuario: cuántas tiene la carpeta
   *  (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). */
  deLaCarpeta?: number;
}): BloqueContenido[] {
  const { carpeta, total, desde, deLaCarpeta } = args;
  const etiqueta = `[ESTILO: "${carpeta.nombre}"]`;
  const elegidas = deLaCarpeta !== undefined;
  if (total === 0) {
    return [
      {
        type: 'text',
        text: elegidas
          ? `Las fotos que el usuario eligió de "${carpeta.nombre}" ya no están en la carpeta. Para verla entera, volvé a llamar con todas: true.`
          : `El estilo "${carpeta.nombre}" está vacío: no tiene fotos.`,
      },
    ];
  }
  if (args.fotos.length === 0) {
    return [
      {
        type: 'text',
        text: `Ese tramo no existe: ${elegidas ? `el usuario eligió ${fotos(total)} de "${carpeta.nombre}"` : `"${carpeta.nombre}" tiene ${fotos(total)}`}. Pedí un \`desde\` entre 1 y ${total}.`,
      },
    ];
  }
  const hasta = desde + args.fotos.length - 1;
  const cuales = elegidas
    ? `fotos ${desde} a ${hasta} de las ${total} que eligió el usuario (la carpeta tiene ${deLaCarpeta}; cada una lleva su número en la carpeta)`
    : `fotos ${desde} a ${hasta} de ${total}`;
  const bloques: BloqueContenido[] = [
    {
      type: 'text',
      text: `${etiqueta} ${cuales}. Son REFERENCIAS DE ESTILO (luz, encuadre, paleta, locación, actitud): sacá de cada una qué aporta. NO copies sus personas, ropa ni productos. Método: skill \`moodboard\`.`,
    },
  ];
  args.fotos.forEach((f, i) => {
    bloques.push({
      type: 'text',
      text: `#${args.numeros?.[i] ?? desde + i} ${etiqueta}${f.url ? ':' : ' (no se pudo cargar)'}`,
    });
    if (f.url) bloques.push({ type: 'image', source: { type: 'url', url: f.url } });
  });
  const quedan = total - hasta;
  if (quedan > 0) {
    bloques.push({
      type: 'text',
      text: `Quedan ${fotos(quedan)}${elegidas ? ' de las elegidas' : ''} que no miraste. Si te hacen falta, volvé a llamar ver_moodboard con desde=${hasta + 1}.`,
    });
  }
  return bloques;
}

/**
 * La herramienta entera, con la red inyectada: `listar` trae las carpetas de la marca y
 * `fotosDe` las fotos de una, ya firmadas (el ORIGINAL, no la miniatura: la firma del
 * original es la que el motor renueva antes de cada turno). `elegidas` dice cuáles eligió
 * el usuario de esa carpeta (`null` = todas): se muestran sólo ésas, salvo `todas: true`.
 */
export async function resolverVerMoodboard(
  input: unknown,
  deps: {
    listar: () => Promise<MoodboardResumen[]>;
    fotosDe: (moodboardId: string) => Promise<FotoDeMoodboard[]>;
    elegidas?: (moodboardId: string) => string[] | null;
  },
): Promise<{ __bloques: BloqueContenido[] }> {
  const carpetas = await deps.listar();
  const pedido = moodboardPedido(input);
  if (!pedido) return { __bloques: bloquesListaDeMoodboards(carpetas) };
  const carpeta = elegirMoodboard(pedido, carpetas);
  if (!carpeta) return { __bloques: bloquesMoodboardNoEncontrado(pedido, carpetas) };
  const todas = await deps.fotosDe(carpeta.id);
  const ids = pideLaCarpetaEntera(input) ? null : (deps.elegidas?.(carpeta.id) ?? null);
  const elegidas = ids ? new Set(ids) : null;
  // Cada foto con su número EN LA CARPETA, aunque se muestren sólo las elegidas.
  const numeradas = todas.map((foto, i) => ({ foto, numero: i + 1 }));
  const vistas = elegidas ? numeradas.filter(({ foto }) => elegidas.has(foto.id)) : numeradas;
  const ventana = ventanaDeFotos(vistas.length, input);
  const tramo = ventana.indices.map((i) => vistas[i]).filter((v): v is (typeof vistas)[number] => Boolean(v));
  return {
    __bloques: bloquesVerMoodboard({
      carpeta,
      total: vistas.length,
      desde: ventana.desde,
      fotos: tramo.map((v) => v.foto),
      numeros: tramo.map((v) => v.numero),
      ...(elegidas ? { deLaCarpeta: todas.length } : {}),
    }),
  };
}

/** La línea del bloque de contexto del mensaje cuando el usuario adjuntó carpetas: un
 *  puntero, como el lote de fotos. Con `fotoIds`, sólo esas fotos de la carpeta.
 *
 *  Va el ID además del nombre, y es el id lo que se le pide que pase: el nombre no
 *  identifica (dos carpetas pueden llamarse igual, y renombrar una después de adjuntarla
 *  rompería la referencia). `elegirMoodboard` resuelve el id primero. */
export function lineaDeContextoDeMoodboards(
  carpetas: { moodboardId: string; name: string; cantidadFotos: number; fotoIds?: string[] }[],
): string {
  const lista = carpetas
    .map((c) => `"${c.name}" (${fotosDelMoodboard(c)}${c.fotoIds ? ' que eligió' : ''}, id=${c.moodboardId})`)
    .join(', ');
  const id = carpetas[0]?.moodboardId ?? '';
  const algunas = carpetas.some((c) => c.fotoIds)
    ? ' De una carpeta con fotos elegidas, ver_moodboard te muestra sólo ésas, cada una con su número en la carpeta.'
    : '';
  return `El usuario adjuntó ${carpetas.length > 1 ? 'los ESTILOS' : 'el ESTILO'} ${lista}: carpetas de referencias de ESTILO para inspirar una campaña. Sus fotos NO viajan en este mensaje: miralas con ver_moodboard pasando el ID en \`moodboard\` (moodboard="${id}"; gratis, de a 10, en esta misma ronda) y cargá la skill \`moodboard\` antes de proponer o generar.${algunas} Son inspiración: no copies sus personas, ropa ni productos.`;
}
